import spacing from '@styles/spacing';
import hasRef from '@toolshed/core-react/utils/has-ref';
import { SearchCard } from 'components/TradeCard/search-card';
import { showCheckaroofBanner } from 'lib/checkaroof/utils';
import {
  breakpointMediumStart,
  spacing2,
  spacing4,
  spacing8,
} from 'lib/mortar';
import React, {
  Fragment,
  type Ref,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import SearchListPlaceholders from './SearchListPlaceholders';
import getTradeTrackingData from './get-trade-tracking-data';
import DisplayAdBanner from './DisplayAdBanner/DisplayAdBanner';
import type { SearchTrade, Trade } from 'lib/sdks/search';
import type { OnTrackEventArgs } from 'containers/AppContainer/AppContainer.interfaces';
import type { setModal } from '../../../containers/AppContainer/actions';
import { Experiment, useOptimizely } from 'lib/sdks/optimizely';
import { getLatestTradeReviewPublic } from 'lib/review/review-api';
import { isShopCategory, getShopBannerConfig } from 'lib/shop';
import { ShopBanner } from 'lib/shop/components/ShopBanner';
import { hasSponsoredBadge } from 'lib/sponsored/badges';

export const Wrapper = styled.div<
  StyledDiv & {
    ref: Ref<HTMLDivElement>;
  }
>`
  display: flex;
  flex-direction: column;
  gap: ${spacing4};
  align-items: flex-start;
  justify-content: flex-start;
  padding-bottom: ${spacing2};

  width: calc(100% + ${spacing8});

  @media screen and (min-width: ${breakpointMediumStart}) {
    width: 100%;
  }
`;

const IMPRESSION_THRESHOLD = 0.15;

export const isVisible = (
  el: HTMLElement | null,
  threshold = 1,
  margins: { top?: number; bottom?: number } = {},
) => {
  if (!el || typeof el.getBoundingClientRect !== 'function') {
    return false;
  }

  const rect = el.getBoundingClientRect();
  const elemTop = rect.top;
  const elemBottom = rect.bottom;
  const elemHeight = rect.height;

  const top = margins.top || 0;
  const bottom = margins.bottom || 0;
  const windowHeight = window.innerHeight - bottom;

  // completely out
  if (elemTop > windowHeight || elemBottom < top) {
    return false;
  }

  // completely visible
  if (threshold === 1) {
    return elemTop >= top && elemBottom <= windowHeight;
  }

  const topHidden = elemTop - top < 0 ? Math.abs(elemTop - top) : 0;
  const bottomHidden =
    windowHeight <= elemBottom ? Math.abs(elemBottom - windowHeight) : 0;

  const leftOver = elemHeight - topHidden - bottomHidden;
  const visible = leftOver / elemHeight;

  return visible >= threshold;
};

export const getVisibleTraders = (
  wrapper: HTMLDivElement | null,
  items: SearchTrade[],
  page: number,
  badges: Record<number, boolean>,
) => {
  if (!wrapper) return null;

  const trades = [];
  for (let i = 0; i < items.length; i++) {
    const el = wrapper.childNodes[i] as HTMLElement;
    const item = items[i];

    if (isVisible(el, IMPRESSION_THRESHOLD, { top: spacing.nav * 2 })) {
      const showRecentlyReviewedBadge = badges[item.companyId] || false;
      trades.push({
        ...getTradeTrackingData(item, showRecentlyReviewedBadge),
        order: i,
        page_number: page,
      });
    }
  }

  return trades;
};

export const createHandleObserverChange = (
  onTrackEvent: (e: OnTrackEventArgs) => void,
  page: number,
  minImpressionTimeMS: number,
  pageSize: number,
  postcode: string,
  categoryId: number,
  location: string,
): [
  (
    e: { isIntersecting: boolean },
    unobserve: () => void,
    data: {
      trade: SearchTrade;
      showRecentlyReviewedBadge: boolean;
    },
    index: number,
  ) => void,
  () => void,
] => {
  const impressionTimers: Record<number, NodeJS.Timeout> = {};
  let listening = false;

  return [
    (
      e: { isIntersecting: boolean },
      unobserve: () => void,
      {
        showRecentlyReviewedBadge,
        trade,
      }: {
        trade: SearchTrade;
        showRecentlyReviewedBadge: boolean;
      },
      index: number,
    ) => {
      if (!listening) {
        if (e.isIntersecting) {
          unobserve();
        }

        return;
      }

      const { companyId } = trade;
      if (e.isIntersecting) {
        impressionTimers[companyId] = setTimeout(() => {
          onTrackEvent({
            key: 'global.trader_impressions',
            data: {
              traders: [
                {
                  ...getTradeTrackingData(trade, showRecentlyReviewedBadge),
                  order: index,
                  page_number: page,
                },
              ],
              page_size: pageSize,
              postcode,
              category: categoryId,
              location,
            },
          });
          unobserve();
        }, minImpressionTimeMS);
      } else {
        clearTimeout(impressionTimers[companyId]);
      }
    },
    // sets listening true to ignore any observer changes before first batch is sent.
    () => {
      listening = true;
    },
  ];
};

const getBiasedRandomInt = (limit: number) => {
  const r1 = Math.random();
  const r2 = Math.random();
  const average = (r1 + r2) / 2;
  return Math.floor(average * limit);
};

export type SearchListProps = {
  operatingLocationText: string;
  loading: unknown;
  itemsPerPage?: number;
  page: number;
  items: SearchTrade[];
  onTrackEvent: (e: OnTrackEventArgs) => void;
  minImpressionTimeMS?: number;
  postcode: string;
  categoryLabel: string;
  categoryId: number;
  location: string;
  source?: string;
  onSetModal: (payload: Parameters<typeof setModal>[0]) => void;
  firstLoadedNewTradeId?: number;
  onMessageClick: (trade: Trade) => void;
  hideContactMessageCta: boolean;
};

const useReviewBadges = () => {
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [badges, setBadges] = useState<Record<number, boolean>>({});
  const [fetchedIds, setFetchedIds] = useState<Set<number>>(new Set());

  const fetchBadges = useCallback(
    async (items: SearchTrade[]) => {
      const filteredCompanyIds = items
        .map(item => item.companyId)
        .filter(id => !fetchedIds.has(id));

      if (filteredCompanyIds.length === 0) return;

      setLoadingBadges(true);
      try {
        const response = await getLatestTradeReviewPublic(filteredCompanyIds);
        setBadges(prev => ({ ...prev, ...(response.data || {}) }));
        setFetchedIds(prev => new Set([...prev, ...filteredCompanyIds]));
      } catch (e) {
      } finally {
        setLoadingBadges(false);
      }
    },
    [fetchedIds],
  );

  return { loadingBadges, badges, fetchBadges };
};

export const SearchList = ({
  operatingLocationText,
  loading,
  itemsPerPage = 10,
  page,
  items,
  onTrackEvent,
  minImpressionTimeMS = 500,
  postcode,
  categoryLabel,
  categoryId,
  location,
  source,
  onSetModal,
  firstLoadedNewTradeId,
  onMessageClick,
  hideContactMessageCta,
}: SearchListProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pageSize = useMemo(() => (items || []).length, [loading]);
  const { isVariantActive } = useOptimizely();
  const { fetchBadges, badges, loadingBadges } = useReviewBadges();

  const shouldLoadBadges = isVariantActive(
    Experiment.SOCIAL_PROOFING_BADGE,
    'variant-a',
  );

  useEffect(() => {
    if (!shouldLoadBadges) return;

    void fetchBadges(items);
  }, [shouldLoadBadges, items, fetchBadges]);

  const banners = useMemo(() => {
    const checkaroofEnabled = showCheckaroofBanner(postcode, categoryId);

    return {
      checkaroof: {
        enabled: checkaroofEnabled,
        position: checkaroofEnabled ? getBiasedRandomInt(pageSize - 1) : null,
        component: checkaroofEnabled
          ? React.lazy(() =>
              import('lib/checkaroof/banner').then(mod => ({
                default: mod.CheckaroofBanner,
              })),
            )
          : null,
      },
      sponsoredDisplay: { position: 4 },
    };
  }, [postcode, categoryId, pageSize]);

  const [handleObserverChange, startListeningToChanges] = useMemo(
    () =>
      createHandleObserverChange(
        onTrackEvent,
        page,
        minImpressionTimeMS,
        pageSize,
        postcode,
        categoryId,
        location,
      ),
    [
      categoryId,
      location,
      minImpressionTimeMS,
      onTrackEvent,
      page,
      pageSize,
      postcode,
    ],
  );

  useEffect(() => {
    if (loading || !hasRef(wrapperRef) || loadingBadges) {
      return undefined;
    }

    const { current } = wrapperRef;
    const traders = getVisibleTraders(current, items, page, badges);
    onTrackEvent({
      key: 'global.trader_impressions',
      data: {
        traders,
        page_size: pageSize,
        postcode,
        category: categoryId,
        location,
      },
    });

    // start listening to other cards intersecting
    const listenTimeout = setTimeout(() => {
      startListeningToChanges();
    }, minImpressionTimeMS / 2);

    return () => {
      clearTimeout(listenTimeout);
    };
  }, [
    badges,
    categoryId,
    items,
    loading,
    loadingBadges,
    location,
    minImpressionTimeMS,
    onTrackEvent,
    page,
    pageSize,
    postcode,
    startListeningToChanges,
  ]);

  return (
    <Wrapper ref={wrapperRef} role="list">
      {/* Show ShopBanner at the top if applicable */}
      {isShopCategory(categoryId) && (
        <ShopBanner {...getShopBannerConfig(categoryId)} />
      )}
      {loading ? (
        <SearchListPlaceholders pageLength={itemsPerPage} />
      ) : (
        items.map((item, index) => {
          const { displayState } = item;

          const { useModal, modalKey } = displayState || {};

          const openModal =
            useModal && modalKey
              ? () => onSetModal({ name: modalKey })
              : undefined;

          const cardProps = {
            operatingLocationText: operatingLocationText,
            onCardAppear: handleObserverChange,
            rootMargin: `-${spacing.nav * 2}px 0px 0px 0px`,
            threshold: IMPRESSION_THRESHOLD,
            onTrackEvent: onTrackEvent,
            order: index,
            companyInformation: item,
            category: categoryLabel,
            onStatusClick: openModal,
            hideContactMessageCta: hideContactMessageCta,
          };

          return (
            <Fragment
              key={`${item.companyId}-${hasSponsoredBadge(item.badges) ? 'sponsored' : 'unsponsored'}`}
            >
              {banners.checkaroof.enabled &&
                index === banners.checkaroof.position && (
                  <Suspense fallback={null}>
                    {banners.checkaroof.component && (
                      <banners.checkaroof.component
                        className="sm:rounded-lg"
                        placement="search"
                        searchParams={
                          new URLSearchParams({ utm_campaign: 'search' })
                        }
                      />
                    )}
                  </Suspense>
                )}
              {index === banners.sponsoredDisplay.position && (
                <Suspense fallback={null}>
                  <DisplayAdBanner
                    postcode={postcode}
                    categoryId={categoryId}
                  />
                </Suspense>
              )}
              <SearchCard
                firstLoadedNewTradeId={firstLoadedNewTradeId}
                onMessageClick={onMessageClick}
                categoryId={categoryId}
                location={postcode}
                showRecentlyReviewedBadge={badges[item.companyId] || false}
                source={source}
                {...cardProps}
              />
            </Fragment>
          );
        })
      )}
    </Wrapper>
  );
};

export default SearchList;
